/* CUSTOM:START custom-header (entire file is custom)
   Behavior for the custom header: closing the expanded search row when the
   shopper moves into the nav, search icon/aria behaviour, flyout close
   buttons, the phone popover and the in-search Ask AI button. The menu itself
   stays driven by the vendor's MainMenu / StoreHeader components in main.js —
   nothing here replaces vendor behavior. The floating chat opener lives in
   assets/custom-chat-opener.js.
   CUSTOM:END custom-header */

(() => {
  'use strict';

  /* ---- Search toggle aria-expanded ----
     The search icons are <button aria-expanded> disclosures (header.liquid).
     Every code path that opens or closes the search row calls this so the
     announced state matches the header's search-is-collapsed class. */

  function setSearchToggleExpanded(header, expanded) {
    header
      .querySelectorAll('.js-show-search, .js-show-search-left')
      .forEach((btn) => btn.setAttribute('aria-expanded', expanded ? 'true' : 'false'));
  }

  /* ---- Close the search row when the shopper moves into the nav ----
     Mirrors StoreHeader.handleSearchToggleClick's close branch (main.js):
     both classes must be restored or the next icon click closes instead of
     opens. Only applies when the header uses a minimised/toggleable search. */

  function closeHeaderSearch() {
    const header = document.querySelector('store-header[data-is-search-minimised="true"]');
    if (!header || header.classList.contains('search-is-collapsed')) return;

    header.classList.remove('search-is-visible');
    setTimeout(() => header.classList.add('search-is-collapsed'));
    setSearchToggleExpanded(header, false);
    document.body.classList.remove('boost-sd__search-opening');
    if (typeof window.setHeaderHeight === 'function') {
      setTimeout(window.setHeaderHeight, 300);
    }
  }

  function initSearchDismissOnMenuHover() {
    const nav = document.querySelector('store-header .main-nav');
    if (!nav || nav.dataset.customSearchDismiss === 'true') return;
    nav.dataset.customSearchDismiss = 'true';

    // Only the top-level dropdown items dismiss the search; leaf links and
    // the rest of the nav leave it alone.
    const isDesktop = () => window.matchMedia(theme.mediaQueries.md).matches;
    const dismiss = () => { if (isDesktop()) closeHeaderSearch(); };
    nav.querySelectorAll(':scope > li.main-nav__item--dropdown').forEach((item) => {
      item.addEventListener('mouseenter', dismiss);
      item.addEventListener('focusin', dismiss);
    });
  }

  /* ---- Instant sibling swap ----
     The mega menu animates only on its first open: while a dropdown is
     already open and the pointer moves to another top-level item, the nav
     gets .custom-nav-swapping and custom-header.css collapses the panel
     transitions so the switch is instant instead of a cross-fade. */

  function initInstantSiblingSwap() {
    const nav = document.querySelector('store-header .main-nav');
    if (!nav || nav.dataset.customInstantSwap === 'true') return;
    nav.dataset.customInstantSwap = 'true';

    // No polls or timeouts here: while .custom-nav-swapping pins the outgoing
    // panel visible, the MutationObserver in initHoverIntentTuning retires it
    // in the same frame the incoming panel gains is-open. The only remaining
    // delay anywhere is the entry hover-intent when nothing is open yet.
    nav.addEventListener('mouseenter', (evt) => {
      const openItem = nav.querySelector('.main-nav__item--dropdown.is-open.is-visible');
      if (!openItem) {
        if (!nav.querySelector('.main-nav__item--dropdown.is-open')) {
          nav.classList.remove('custom-nav-swapping');
        }
        return;
      }
      if (openItem.contains(evt.target)) return;
      const li = evt.target.closest('.main-nav > li');
      if (!li) return;

      if (li.matches('.main-nav__item--dropdown')) {
        nav.classList.add('custom-nav-swapping');
      } else {
        // Leaf link: nothing new will open. Close via the vendor's Escape
        // path so the overlay is released too — stripping classes directly
        // leaves the vendor's later leaf handler finding nothing open and
        // skipping its toggleOverlay(false), stranding the dark overlay.
        nav.classList.remove('custom-nav-swapping');
        nav.dispatchEvent(new KeyboardEvent('keyup', { key: 'Escape', bubbles: true }));
      }
    }, true);

    nav.addEventListener('mouseleave', () => nav.classList.remove('custom-nav-swapping'));
  }

  /* ---- Search reset on breakpoint change ----
     An expanded (and especially focused) search carried across the md
     breakpoint strands its body classes: predictive-search's
     overlay-predictive-search and Boost's search-opening / g-no-scroll lock
     the page with no close path on the other side of the breakpoint (the
     lifecycle that added them is desktop-only). Collapse the search and clear
     the locks whenever the breakpoint flips. */

  function initSearchBreakpointReset() {
    const header = document.querySelector('store-header[data-is-search-minimised="true"]');
    if (!header || header.dataset.customBreakpointReset === 'true') return;
    header.dataset.customBreakpointReset = 'true';

    window.addEventListener('on:breakpoint-change', () => {
      if (header.classList.contains('search-is-collapsed')) return;
      header.classList.remove('search-is-visible');
      header.classList.add('search-is-collapsed');
      setSearchToggleExpanded(header, false);
      header.querySelector('.js-search-input')?.blur();
      header.querySelector('predictive-search')?.close?.();
      document.body.classList.remove(
        'overlay-predictive-search',
        'boost-sd__search-opening',
        'boost-sd__g-no-scroll'
      );
      if (typeof window.setHeaderHeight === 'function') setTimeout(window.setHeaderHeight, 50);
    });
  }

  /* ---- Search icon behavior per breakpoint ----
     Desktop pointer clicks open the row WITHOUT focusing the input — the
     vendor focuses it 300ms after opening and predictive-search/Boost open
     their suggestion panel on that focus, popping it uninvited. Keyboard
     activations (click detail 0) keep the vendor path so focus lands in the
     input for accessibility.
     Mobile taps do the opposite: expand and focus synchronously, so the
     full-page search overlay (and the phone keyboard, which requires focus
     inside the tap gesture) opens in one tap instead of tap-icon-then-tap-
     input. */

  function initSearchIconBehavior() {
    const header = document.querySelector('store-header[data-is-search-minimised="true"]');
    if (!header || header.dataset.customSearchIcon === 'true') return;
    header.dataset.customSearchIcon = 'true';

    const closeSearch = () => {
      header.classList.remove('search-is-visible');
      setTimeout(() => header.classList.add('search-is-collapsed'));
      setSearchToggleExpanded(header, false);
      header.querySelector('predictive-search')?.close?.();
      document.body.classList.remove(
        'boost-sd__search-opening',
        'boost-sd__g-no-scroll',
        'overlay-predictive-search'
      );
      if (typeof window.setHeaderHeight === 'function') setTimeout(window.setHeaderHeight, 300);
    };

    header.addEventListener('click', (evt) => {
      const trigger = evt.target.closest('.js-show-search');
      if (!trigger || evt.detail === 0) return;
      evt.preventDefault();
      evt.stopPropagation();

      const isDesktop = window.matchMedia(theme.mediaQueries.md).matches;

      if (header.classList.contains('search-is-collapsed')) {
        setSearchToggleExpanded(header, true);
        if (isDesktop) {
          header.classList.remove('search-is-collapsed');
          setTimeout(() => {
            header.classList.add('search-is-visible');
            if (typeof window.setHeaderHeight === 'function') window.setHeaderHeight();
          }, 300);
        } else {
          header.classList.remove('search-is-collapsed');
          header.classList.add('search-is-visible');
          header.querySelector('.js-search-input')?.focus();
          if (typeof window.setHeaderHeight === 'function') window.setHeaderHeight();
        }
      } else {
        closeSearch();
      }
    }, true);

    /* Escape closes the search row and returns focus to the toggle that
       opened it. Two-stage, per the combobox pattern: while predictive-search
       has its suggestions open, the first Escape only dismisses those (the
       vendor's own handler), and the next one closes the row. Capture phase so
       we read the suggestions' open state before the vendor's handler clears it. */
    const focusSearchToggle = () => {
      const toggle = Array.from(
        header.querySelectorAll('.js-show-search, .js-show-search-left')
      ).find((btn) => btn.getClientRects().length > 0);
      toggle?.focus();
    };

    header.addEventListener('keydown', (evt) => {
      if (evt.key !== 'Escape' || header.classList.contains('search-is-collapsed')) return;
      const inSearch = evt.target.closest('.js-search-bar, .js-show-search, .js-show-search-left');
      if (!inSearch) return;
      if (header.querySelector('predictive-search[open]')) return;
      evt.preventDefault();
      closeSearch();
      focusSearchToggle();
    }, true);
  }

  /* ---- Hover-intent tuning + click grace period ----
     1) The vendor delays every dropdown open by data-menu-sensitivity (200ms),
        read from the DOM at each mouseenter. While a menu is already open and
        the nav occupies a single row, we set it to 0 so sibling switches are
        instant; entering the nav from outside (nothing open) keeps the full
        delay, and a wrapped (two-row) nav keeps it too, since vertical mouse
        travel crosses items accidentally.
     2) The vendor treats a click on an open menu's label as toggle-close, so
        "enter nav, click quickly" opens via hover and instantly closes via the
        click. A capture-phase listener swallows the toggle-close for 600ms
        after a menu opens; deliberate label-clicks to close still work after
        that. */

  function initHoverIntentTuning() {
    const nav = document.querySelector('store-header .main-nav');
    const mainMenu = document.querySelector('store-header main-menu');
    if (!nav || !mainMenu || mainMenu.dataset.customHoverTuning === 'true') return;
    mainMenu.dataset.customHoverTuning = 'true';

    const baseSensitivity = mainMenu.dataset.menuSensitivity || '200';

    const navIsSingleRow = () => {
      const items = [...nav.querySelectorAll(':scope > li')].filter((li) => li.offsetParent !== null);
      return new Set(items.map((li) => li.offsetTop)).size <= 1;
    };

    const syncSensitivity = () => {
      const anyOpen = nav.querySelector('.main-nav__item--dropdown.is-open');
      mainMenu.dataset.menuSensitivity = anyOpen && navIsSingleRow() ? '0' : baseSensitivity;
    };

    // Track opens (sensitivity + click grace period), and enforce the
    // single-open invariant synchronously: the moment a dropdown gains
    // is-open on desktop, every other open one is retired in the same frame.
    // This replaces any timeout/poll-based retirement — a taller outgoing
    // panel can never linger under a shorter incoming one.
    new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (!m.target.matches?.('.main-nav__item--dropdown')) continue;
        const wasOpen = (m.oldValue || '').split(/\s+/).includes('is-open');
        if (!wasOpen && m.target.classList.contains('is-open')) {
          m.target.dataset.customOpenedAt = String(Date.now());
          if (window.matchMedia(theme.mediaQueries.md).matches) {
            nav.querySelectorAll('.main-nav__item--dropdown.is-open').forEach((other) => {
              if (other !== m.target) other.classList.remove('is-open', 'is-visible');
            });
          }
        }
      }
      syncSensitivity();
    }).observe(nav, { attributes: true, attributeFilter: ['class'], attributeOldValue: true, subtree: true });

    nav.addEventListener('click', (evt) => {
      if (!window.matchMedia(theme.mediaQueries.md).matches) return;
      const li = evt.target.closest('.main-nav__item--dropdown');
      if (!li || !li.classList.contains('is-open')) return;
      if (evt.target.closest('.main-nav__child')) return; // clicks inside the panel are fine
      const openedAt = Number(li.dataset.customOpenedAt || 0);
      if (Date.now() - openedAt < 600) {
        evt.preventDefault();
        evt.stopPropagation();
      }
    }, true);
  }

  /* ---- Nav click state normalizer ----
     The vendor's click toggle (main.js handleNavClick) defers state: close()
     removes is-open only on a transitionend, open() adds is-visible on a
     requestAnimationFrame. Two fast clicks interleave those and can leave a
     dropdown with mismatched is-open/is-visible ("zombie"). The vendor's
     hover-open then treats the zombie as the active menu and never closes the
     panel that is actually visible. After every desktop nav click, settle the
     state once the vendor's deferred steps have run. */

  function initNavClickNormalizer() {
    const nav = document.querySelector('store-header .main-nav');
    if (!nav || nav.dataset.customClickNormalizer === 'true') return;
    nav.dataset.customClickNormalizer = 'true';

    // Mobile accordion rows: the vendor's close removes is-open only on a
    // height transitionend; an interrupted/coalesced transition leaves the
    // row wedged (is-open with rendered height 0), after which every close
    // is a 0->0 no-op that never fires the event. aria-expanded is set
    // synchronously and is the source of truth: converge class + inline
    // height to it once the animation window has passed.
    nav.addEventListener('click', (evt) => {
      if (window.matchMedia(theme.mediaQueries.md).matches) return;
      const btn = evt.target.closest('.main-nav__toggle-btn');
      const row = btn && btn.closest('.child-nav__item--toggle');
      if (!row) return;
      setTimeout(() => {
        const shouldBeOpen = btn.getAttribute('aria-expanded') === 'true';
        if (row.classList.contains('is-open') !== shouldBeOpen) {
          row.classList.toggle('is-open', shouldBeOpen);
          const panel = row.querySelector(':scope > .child-nav__panel');
          if (panel) panel.style.height = '';
        }
      }, 500);
    });

    nav.addEventListener('click', (evt) => {
      if (!window.matchMedia(theme.mediaQueries.md).matches) return;
      const li = evt.target.closest('.main-nav__item--dropdown');
      if (!li) return;

      requestAnimationFrame(() => requestAnimationFrame(() => {
        const opening = li.classList.contains('is-open') && li.classList.contains('is-visible');
        if (opening) {
          // Only one dropdown may be open.
          nav.querySelectorAll('.main-nav__item--dropdown.is-open').forEach((other) => {
            if (other !== li) other.classList.remove('is-open', 'is-visible');
          });
        } else if (li.classList.contains('is-open')) {
          // Closing: guarantee completion even if the exit transitionend
          // never fires (reduced motion, interrupted transition).
          setTimeout(() => {
            if (li.classList.contains('is-open') && !li.classList.contains('is-visible')) {
              li.classList.remove('is-open');
            }
          }, 450);
        } else {
          li.classList.remove('is-visible');
        }
      }));
    });
  }

  /* ---- Mega menu close button ----
     Closes via the vendor's Escape path (overlay, aria-expanded and focus
     handling all included) and returns focus to the menu's top-level link so
     keyboard focus never strands inside the hidden panel. */

  function initFlyoutCloseButtons() {
    const nav = document.querySelector('store-header .main-nav');
    if (!nav || nav.dataset.customFlyoutClose === 'true') return;
    nav.dataset.customFlyoutClose = 'true';

    nav.addEventListener('click', (evt) => {
      const btn = evt.target.closest('.js-custom-flyout-close');
      if (!btn) return;
      evt.preventDefault();
      const li = btn.closest('.main-nav__item--dropdown');
      nav.dispatchEvent(new KeyboardEvent('keyup', { key: 'Escape', bubbles: true }));
      li?.querySelector('.main-nav__item--primary')?.focus();
    });
  }

  /* ---- Phone popover: Escape dismissal (WCAG 1.4.13) ----
     The popover is CSS hover/focus-within; Escape hides it via a class
     without moving focus, and leaving with pointer/focus re-arms it. */

  function initPhonePopoverEscape() {
    const pop = document.querySelector('.custom-phone-popover');
    if (!pop || pop.dataset.escDismiss === 'true') return;
    pop.dataset.escDismiss = 'true';

    document.addEventListener('keydown', (evt) => {
      if (evt.key !== 'Escape') return;
      if (pop.matches(':hover') || pop.matches(':focus-within')) {
        pop.classList.add('custom-phone-popover--dismissed');
      }
    });

    const rearm = () => pop.classList.remove('custom-phone-popover--dismissed');
    pop.addEventListener('mouseleave', rearm);
    pop.addEventListener('focusout', rearm);
  }

  /* ---- Ask AI button (only used when its link setting is blank) ----
     Opens Envive through the chat opener API when it is on; otherwise clicks
     Envive's own (possibly hidden) floating button. */

  function initAskAiButton() {
    const btn = document.querySelector('.js-custom-ask-ai');
    if (!btn || btn.dataset.initialized === 'true') return;
    btn.dataset.initialized = 'true';
    btn.addEventListener('click', () => {
      if (window.sylChat) {
        window.sylChat.open('ai', { source: 'header' });
        return;
      }
      const host = document.getElementById('envive-ai-floating-chat');
      const root = host && (host.shadowRoot || host);
      const nativeBtn = root && root.querySelector('#envive-ai-floating-button button, button');
      if (nativeBtn) nativeBtn.click();
    });
  }

  /* ---- Sticky header offset ----
     theme.liquid hardcodes --header-top: 32px (the announcement bar's
     one-line height) for the sticky header's `top`. When the announcement
     text wraps to two or more lines at narrower widths, the bar is taller
     than 32px and the sticky header slides underneath it on scroll. Measure
     the bar's real height instead, and keep it current across resizes and
     text wrapping. Only runs when the bar is sticky (the non-sticky setting
     uses -1px, which must stay as is). */

  function initStickyHeaderOffset() {
    // Section-group ids are prefixed (shopify-section-sections--<id>__announcement),
    // so select by the section's schema class rather than by id.
    const announcement = document.querySelector('.shopify-section.cc-announcement');
    const root = document.documentElement;
    if (!announcement) {
      root.style.removeProperty('--header-top');
      return;
    }
    if (getComputedStyle(announcement).position !== 'sticky') return;

    const update = () => {
      root.style.setProperty('--header-top', `${announcement.offsetHeight}px`);
    };
    update();

    if (announcement.dataset.customOffsetObserved === 'true') return;
    announcement.dataset.customOffsetObserved = 'true';

    if ('ResizeObserver' in window) {
      new ResizeObserver(update).observe(announcement);
    } else {
      window.addEventListener('resize', update);
    }
  }

  function init() {
    initStickyHeaderOffset();
    initSearchDismissOnMenuHover();
    initInstantSiblingSwap();
    initSearchBreakpointReset();
    initSearchIconBehavior();
    initHoverIntentTuning();
    initNavClickNormalizer();
    initFlyoutCloseButtons();
    initPhonePopoverEscape();
    initAskAiButton();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
  }
})();
